import { useState } from 'react';
import type { AdminUser, Id, Project } from '../../api/types';
import { projectsAvailableTo, type UserProject } from './membership';

/**
 * WHICH PROJECTS THIS PERSON IS IN — PH-56, Or's *"let admin manage which project they are in also"*.
 *
 * ⭐⭐ THE EMPTY STATE IS THE MOST IMPORTANT THING ON THIS COMPONENT, which is why it is a sentence
 * and not a blank area. "In no project" was the actual state of four humans today, and it is the
 * cause of every confusing permission error PlanHub has produced: a release refused, the MCP's 403
 * writes, two people unable to act. Each surfaced as *"only an active member of this project may…"*
 * — an error about a rule rather than about the person's missing membership. Saying it here, on the
 * screen an admin already visits, is the whole point of the feature.
 *
 * ⛔ ROLE IS SHOWN, NOT JUST PROJECT. `member` and `lead` differ, and an admin deciding whether it is
 * safe to remove someone needs to see which they are before pressing anything.
 */
export function UserProjects({
  user,
  memberships,
  allProjects,
  busy,
  loading,
  onAdd,
  onRemove,
}: {
  user: AdminUser;
  /** ⚠️ `undefined` means "in no project" — see `invertMemberships`, which omits such users. */
  memberships: UserProject[] | undefined;
  allProjects: readonly Project[];
  busy: boolean;
  /**
   * 🔴 LOADING IS A THIRD STATE, AND CONFLATING IT WITH "IN NO PROJECT" WAS A REAL DEFECT IN THE
   * FIRST VERSION OF THIS FILE. The section renders as soon as the USER list arrives, but its
   * contents need the project list and then every project's members. Until those land, `memberships`
   * is `undefined` — which is the SAME value that means "this person is in no project".
   * ⇒ For a second, every user was accused of being in no project. A test caught it; on screen it
   *   would have looked like a fact rather than a delay.
   * ⭐ Same family as everything else here: an ABSENCE rendered as a CLAIM.
   */
  loading: boolean;
  onAdd: (projectId: Id, role: 'member' | 'lead') => void;
  onRemove: (projectId: Id, projectKey: string) => void;
}) {
  const current = memberships ?? [];
  const available = projectsAvailableTo(allProjects, current);
  const [projectId, setProjectId] = useState('');
  const [role, setRole] = useState<'member' | 'lead'>('member');

  const submit = () => {
    if (projectId === '') return;
    onAdd(projectId, role);
    setProjectId('');
    setRole('member');
  };

  return (
    <section aria-label={`Projects for ${user.display_name}`} className="mt-2">
      <h4 className="text-xs font-semibold text-[color:var(--color-ink-muted)]">Projects</h4>

      {loading ? (
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">Loading projects…</p>
      ) : current.length === 0 ? (
        /* 🔴 Not an empty div, and not "—". This sentence is the feature. */
        <p className="mt-1 text-xs text-[color:var(--color-warning,#b45309)]">
          In no project. They can sign in, but every write will be refused with{' '}
          <em>only an active member of this project may…</em> until they are added to one.
        </p>
      ) : (
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {current.map((entry) => (
            <li
              key={String(entry.project.id)}
              className="flex items-center gap-1 rounded border border-[color:var(--color-line)] px-1.5 py-0.5 text-xs"
            >
              <span className="font-mono font-semibold">{entry.project.key}</span>
              <span className="text-[color:var(--color-ink-muted)]">{entry.role}</span>
              <button
                type="button"
                onClick={() => onRemove(entry.project.id, entry.project.key)}
                disabled={busy}
                aria-label={`Remove ${user.display_name} from ${entry.project.key}`}
                className="ml-0.5 rounded px-1 text-[color:var(--color-ink-muted)] hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/10"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ⛔ The picker DISAPPEARS when there is nothing to add, rather than rendering an empty
          select. An empty dropdown reads as "loading" or "broken"; its absence reads as "they are
          already in everything", which is what is true. */}
      {!loading && available.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <label className="sr-only" htmlFor={`add-project-${user.id}`}>
            Add {user.display_name} to a project
          </label>
          <select
            id={`add-project-${user.id}`}
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            disabled={busy}
            className="h-8 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-1.5 text-xs disabled:opacity-50"
          >
            <option value="">Add to project…</option>
            {available.map((project) => (
              <option key={String(project.id)} value={String(project.id)}>
                {project.key} — {project.name}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor={`add-role-${user.id}`}>
            Role for {user.display_name}
          </label>
          <select
            id={`add-role-${user.id}`}
            value={role}
            onChange={(event) => setRole(event.target.value as 'member' | 'lead')}
            disabled={busy}
            className="h-8 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-1.5 text-xs disabled:opacity-50"
          >
            <option value="member">member</option>
            <option value="lead">lead</option>
          </select>

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-8 rounded border border-[color:var(--color-line)] px-2 text-xs font-medium disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
